import React from 'react';
import { isDependencyConflict } from '@/components/deleteConfirmDialog';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { canNavigateBack } from '@/utils';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ProductUnit, VatType } from '@/api/models';
import {
    useBrandListQuery,
    useCategoryPickerQuery,
    useDeleteProductMutation,
    useSetProductArchivedMutation,
    useProductDetailQuery,
    useUpdateProductMutation,
} from '@/api/queries';
import {
    flattenPickerCategories,
    type CategoryFilterOption,
} from '../productsMeta';
import type { SimpleOption } from '../createProduct/useCreateProductForm';
import type { CreateSubmitError } from '../createProduct/useCreateProductForm';
import { readProductLoad } from '../productDetail/productDetailMeta';
import { stepsBackToOrigin } from '../productOrigin';
import {
    buildUpdateRequest,
    seedEditForm,
    validateEditForm,
    type EditProductFormState,
    type EditVariantDraft,
} from './editProductMeta';

/** Editable string fields on an edit-variant draft. */
export type EditVariantField =
    | 'salePrice'
    | 'wholesalePrice'
    | 'minimumQuantity'
    | 'weight'
    | 'volume'
    | 'saleEndDate';

/**
 * Drives the fields-only edit form. Loads the product graph, seeds an editable
 * draft once, and PUTs metadata + existing-variant fields. It never exposes
 * controls to change axes, add/remove variants, or touch batches — the draft
 * only carries what `PUT /products/{id}` accepts. Saving or cancelling returns
 * to the detail page without leaving the form in history; delete and archive
 * return to where the product was opened, normally the filtered list. Server
 * 400 text is surfaced inline.
 */
export const useEditProductForm = () => {
    const params = useParams();
    const productId = params.productId ?? null;
    const navigate = useNavigate();
    const location = useLocation();

    // A pending delete/archive may finish after this route has been left.
    // Only its original edit workflow may change feedback or navigation.
    const workflowRef = React.useRef<symbol | null>(null);
    React.useEffect(() => {
        workflowRef.current = Symbol();
        return () => {
            workflowRef.current = null;
        };
    }, [location.key, productId]);

    const detailQuery = useProductDetailQuery(productId);
    const updateMutation = useUpdateProductMutation();
    const deleteMutation = useDeleteProductMutation();
    const archiveMutation = useSetProductArchivedMutation();

    /**
     * Whether the last delete was refused because live data depends on the
     * product (bos-api BOS-121) — stock on hand, or sale and purchase lines.
     * The remedy differs: stock has to be written off in the warehouse, and
     * history is dealt with by archiving. The server's message says which; this
     * only decides whether to offer the shortcut.
     */
    const [deleteConflict, setDeleteConflict] =
        React.useState<ErrorResponse | null>(null);
    const categoryPickerQuery = useCategoryPickerQuery();
    const brandListQuery = useBrandListQuery();

    const [state, setState] = React.useState<EditProductFormState | null>(null);
    const [submitError, setSubmitError] =
        React.useState<CreateSubmitError | null>(null);

    const loadedProduct =
        detailQuery.data && detailQuery.data.success
            ? detailQuery.data.response
            : null;
    // A product that no longer exists is `notFound`, not a load error: there
    // is nothing to try again (bos-app#136).
    const load = readProductLoad(detailQuery.data);
    const loadError: ErrorResponse | null =
        load.status === 'error' ? load.error : null;

    // Seed the draft once the product has loaded (re-seed if the id changes).
    const seededIdRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (loadedProduct && seededIdRef.current !== loadedProduct.id) {
            seededIdRef.current = loadedProduct.id;
            setState(seedEditForm(loadedProduct));
        }
    }, [loadedProduct]);

    const categoryOptions = React.useMemo<CategoryFilterOption[]>(() => {
        const result = categoryPickerQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return flattenPickerCategories(result.response.items);
    }, [categoryPickerQuery.data]);

    const brandOptions = React.useMemo<SimpleOption[]>(() => {
        const result = brandListQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return result.response.items.map((brand) => ({
            id: brand.id,
            name: brand.name,
        }));
    }, [brandListQuery.data]);

    const patchState = React.useCallback(
        (update: Partial<EditProductFormState>) => {
            setState((prev) => (prev ? { ...prev, ...update } : prev));
        },
        [],
    );

    const onNameChange = React.useCallback(
        (name: string) => patchState({ name }),
        [patchState],
    );
    const onSkuChange = React.useCallback(
        (sku: string) => patchState({ sku }),
        [patchState],
    );
    const onUnitChange = React.useCallback(
        (unit: ProductUnit) => patchState({ unit }),
        [patchState],
    );
    const onVatChange = React.useCallback(
        (vatType: VatType) => patchState({ vatType }),
        [patchState],
    );
    const onCategoryChange = React.useCallback(
        (categoryId: string | null) => patchState({ categoryId }),
        [patchState],
    );
    const onBrandChange = React.useCallback(
        (brandId: string | null) => patchState({ brandId }),
        [patchState],
    );

    const onVariantFieldChange = React.useCallback(
        (variantId: string, field: EditVariantField, value: string) => {
            setState((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    variants: prev.variants.map((variant) =>
                        variant.id === variantId
                            ? ({
                                  ...variant,
                                  [field]: value,
                              } as EditVariantDraft)
                            : variant,
                    ),
                };
            });
        },
        [],
    );

    /**
     * Leaves the form, saved or not, for the product's detail page. The detail
     * page is the only in-app way in, so with history behind the form this
     * steps back to it: list → detail → edit becomes list → detail, and the
     * detail page's back button reaches the list. Pushing would stack the form
     * under the detail page, and replacing would leave two detail entries in a
     * row. Opened directly there is nothing to step back to, so the detail page
     * takes over the form's entry instead.
     */
    const returnToDetail = React.useCallback(() => {
        if (canNavigateBack()) {
            navigate(-1);
            return;
        }
        navigate(
            productId ? PATH.PRODUCTS.BIND(productId) : PATH.PRODUCTS.ROOT,
            {
                replace: true,
            },
        );
    }, [navigate, productId]);

    /**
     * Leaves a product that is gone from the list, deleted or archived instead,
     * for the entry that opened its details page (bos-app#137). Going back
     * through history to it restores the list's address exactly, search,
     * filters, branch and page, and takes the product's details and edit
     * entries off the back stack, so Back never reaches it. With no origin to
     * go back to (a deep link, a new tab, a lost state) the unfiltered list
     * replaces the form instead.
     */
    const returnToOrigin = React.useCallback(() => {
        const steps = stepsBackToOrigin(location.state);
        if (steps === null) {
            navigate(PATH.PRODUCTS.ROOT, { replace: true });
            return;
        }
        navigate(-steps);
    }, [location.state, navigate]);

    const onSubmit = React.useCallback(async () => {
        if (!state || !productId) {
            return;
        }
        setSubmitError(null);
        const validationKey = validateEditForm(state);
        if (validationKey) {
            setSubmitError({ kind: 'i18n', key: validationKey });
            return;
        }
        const result = await updateMutation.mutateAsync({
            id: productId,
            body: buildUpdateRequest(state),
        });
        if (!result.success) {
            setSubmitError({
                kind: 'raw',
                message:
                    result.error.message ??
                    'Failed to save the product. Please try again.',
            });
            return;
        }
        returnToDetail();
    }, [productId, returnToDetail, state, updateMutation]);

    const onDelete = React.useCallback(async () => {
        if (!productId) {
            return;
        }
        setSubmitError(null);
        setDeleteConflict(null);
        const workflow = workflowRef.current;
        const result = await deleteMutation.mutateAsync(productId);
        if (workflow === null || workflowRef.current !== workflow) {
            return;
        }
        if (!result.success) {
            setSubmitError({
                kind: 'raw',
                message:
                    result.error.message ??
                    'Failed to delete the product. Please try again.',
            });
            // The refusal already reads as a sentence with a remedy in it; the
            // flag is only there to decide whether to offer the shortcut.
            if (isDependencyConflict(result.error)) {
                setDeleteConflict(result.error);
            }
            return;
        }
        returnToOrigin();
    }, [deleteMutation, productId, returnToOrigin]);

    const onArchiveInstead = React.useCallback(async () => {
        if (!productId) {
            return;
        }
        setSubmitError(null);
        const workflow = workflowRef.current;
        const result = await archiveMutation.mutateAsync({
            id: productId,
            archived: true,
        });
        if (workflow === null || workflowRef.current !== workflow) {
            return;
        }
        if (!result.success) {
            setSubmitError({
                kind: 'raw',
                message:
                    result.error.message ??
                    'Failed to archive the product. Please try again.',
            });
            return;
        }
        returnToOrigin();
    }, [archiveMutation, productId, returnToOrigin]);

    const onRetry = React.useCallback(() => {
        detailQuery.refetch();
    }, [detailQuery]);

    return {
        /** Blockers named by a refused delete, or null. */
        deleteConflict,
        isArchiving: archiveMutation.isPending,
        onArchiveInstead,
        productId,
        state,
        /** The product request: `notFound` when the product no longer exists (bos-app#136). */
        loadStatus: load.status,
        isLoading: detailQuery.isLoading,
        /** A load failure Try again can recover from; never a missing product. */
        loadError,
        submitError,
        isSubmitting: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
        categoryOptions,
        brandOptions,
        onNameChange,
        onSkuChange,
        onUnitChange,
        onVatChange,
        onCategoryChange,
        onBrandChange,
        onVariantFieldChange,
        onSubmit,
        /** Leaves without saving — the Cancel and "Back to product" actions. */
        onCancel: returnToDetail,
        onDelete,
        onRetry,
    };
};
